import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "./store";
import messageService, { 
  Message, 
  ChatUser, 
  NegotiationDetails,
  AcceptResponse 
} from "../services/message_service";

// State interface for the messages slice
interface MessageState {
  messages: {
    [conversationKey: string]: Message[];
  };
  chats: ChatUser[];
  selectedConversation: {
    userId: number | null;
    messages: Message[];
    loading: boolean;
  };
  loading: boolean;
  error: string | null;
  negotiationInProgress: boolean;
  negotiationError: string | null;
  pendingPayment: AcceptResponse | null;
}

// Initial state
const initialState: MessageState = {
  messages: {},
  chats: [],
  selectedConversation: {
    userId: null,
    messages: [],
    loading: false
  },
  loading: false,
  error: null,
  negotiationInProgress: false,
  negotiationError: null,
  pendingPayment: null
};

// Helper function to generate conversation key
const getConversationKey = (userId1: number, userId2: number): string => {
  return userId1 < userId2 
    ? `${userId1}_${userId2}` 
    : `${userId2}_${userId1}`;
};

// Async thunks for API calls
export const fetchChats = createAsyncThunk(
  'messages/fetchChats',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await messageService.getChats(userId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch chats');
    }
  }
);

export const fetchConversation = createAsyncThunk(
  'messages/fetchConversation',
  async ({ userId, otherUserId }: { userId: number, otherUserId: number }, { rejectWithValue }) => {
    try {
      const messages = await messageService.getConversation(userId, otherUserId);
      return { messages, otherUserId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch conversation');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async (message: Omit<Message, 'id' | 'createdAt' | 'status'>, { rejectWithValue }) => {
    try {
      return await messageService.sendMessage(message);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'messages/deleteMessage',
  async (messageId: number, { rejectWithValue }) => {
    try {
      await messageService.deleteMessage(messageId);
      return messageId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete message');
    }
  }
);

export const negotiateMessage = createAsyncThunk(
  'messages/negotiateMessage',
  async ({ messageId, details }: { messageId: number, details: NegotiationDetails }, { rejectWithValue }) => {
    try {
      return await messageService.negotiateMessage(messageId, details);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to negotiate message');
    }
  }
);

export const acceptNegotiation = createAsyncThunk(
  'messages/acceptNegotiation',
  async (messageId: number, { rejectWithValue }) => {
    try {
      return await messageService.acceptNegotiation(messageId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to accept negotiation');
    }
  }
);

export const rejectNegotiation = createAsyncThunk(
  'messages/rejectNegotiation',
  async (messageId: number, { rejectWithValue }) => {
    try {
      return await messageService.rejectNegotiation(messageId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to reject negotiation');
    }
  }
);

// Create the messages slice
const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    clearSelectedConversation: (state) => {
      state.selectedConversation = {
        userId: null,
        messages: [],
        loading: false
      };
    },
    clearNegotiationState: (state) => {
      state.negotiationInProgress = false;
      state.negotiationError = null;
    },
    clearPendingPayment: (state) => {
      state.pendingPayment = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Handle fetchChats
    builder
      .addCase(fetchChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChats.fulfilled, (state, action) => {
        state.loading = false;
        state.chats = action.payload;
      })
      .addCase(fetchChats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
    
    // Handle fetchConversation
    builder
      .addCase(fetchConversation.pending, (state) => {
        state.selectedConversation.loading = true;
        state.error = null;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.selectedConversation = {
          userId: action.payload.otherUserId,
          messages: action.payload.messages,
          loading: false
        };
        // Also store in the conversations cache
        if (state.selectedConversation.userId && action.meta.arg.userId) {
          const key = getConversationKey(action.meta.arg.userId, action.payload.otherUserId);
          state.messages[key] = action.payload.messages;
        }
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.selectedConversation.loading = false;
        state.error = action.payload as string;
      })
    
    // Handle sendMessage
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        
        // Add message to current conversation if appropriate
        if (state.selectedConversation.userId === action.payload.receiverId) {
          state.selectedConversation.messages.push(action.payload);
        }
        
        // Add to conversations cache
        const key = getConversationKey(action.payload.senderId, action.payload.receiverId);
        if (!state.messages[key]) {
          state.messages[key] = [];
        }
        state.messages[key].push(action.payload);
        
        // Update chats list if needed
        const existingChatIndex = state.chats.findIndex(
          chat => chat.id === action.payload.receiverId
        );
        
        if (existingChatIndex >= 0) {
          const chat = state.chats[existingChatIndex];
          // Only proceed if chat is defined
          if (chat) {
            // Update last message for existing chat
            chat.lastMessage = action.payload;
            // Move this chat to the top
            if (existingChatIndex > 0) {
              state.chats.splice(existingChatIndex, 1);
              state.chats.unshift(chat);
            }
          }
        }
        // Note: If chat doesn't exist, it should be created on next fetchChats call
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
    
    // Handle deleteMessage
    builder
      .addCase(deleteMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.loading = false;
        
        // Remove from current conversation if present
        if (state.selectedConversation.userId) {
          state.selectedConversation.messages = state.selectedConversation.messages.filter(
            msg => msg.id !== action.payload
          );
        }
        
        // Remove from conversations cache
        Object.keys(state.messages).forEach(key => {
          if (state.messages[key]) { // Check if array exists
            state.messages[key] = state.messages[key].filter(
              msg => msg.id !== action.payload
            );
          }
        });
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
    
    // Handle negotiateMessage
    builder
      .addCase(negotiateMessage.pending, (state) => {
        state.negotiationInProgress = true;
        state.negotiationError = null;
      })
      .addCase(negotiateMessage.fulfilled, (state, action) => {
        state.negotiationInProgress = false;
        
        // Update message in current conversation if present
        if (state.selectedConversation.userId) {
          const messageIndex = state.selectedConversation.messages.findIndex(
            msg => msg.id === action.payload.id
          );
          if (messageIndex >= 0) {
            state.selectedConversation.messages[messageIndex] = action.payload;
          }
        }
        
        // Update in conversations cache
        Object.keys(state.messages).forEach(key => {
          const messagesArray = state.messages[key];
          if (messagesArray) { // Check if array exists
            const messageIndex = messagesArray.findIndex(
              msg => msg.id === action.payload.id
            );
            if (messageIndex >= 0) {
              messagesArray[messageIndex] = action.payload;
            }
          }
        });
      })
      .addCase(negotiateMessage.rejected, (state, action) => {
        state.negotiationInProgress = false;
        state.negotiationError = action.payload as string;
      })
    
    // Handle acceptNegotiation
    builder
      .addCase(acceptNegotiation.pending, (state) => {
        state.negotiationInProgress = true;
        state.negotiationError = null;
      })
      .addCase(acceptNegotiation.fulfilled, (state, action) => {
        state.negotiationInProgress = false;
        state.pendingPayment = action.payload;
      })
      .addCase(acceptNegotiation.rejected, (state, action) => {
        state.negotiationInProgress = false;
        state.negotiationError = action.payload as string;
      })
    
    // Handle rejectNegotiation
    builder
      .addCase(rejectNegotiation.pending, (state) => {
        state.negotiationInProgress = true;
        state.negotiationError = null;
      })
      .addCase(rejectNegotiation.fulfilled, (state, action) => {
        state.negotiationInProgress = false;
        
        // Update message in current conversation if present
        if (state.selectedConversation.userId) {
          const messageIndex = state.selectedConversation.messages.findIndex(
            msg => msg.id === action.payload.id
          );
          if (messageIndex >= 0) {
            state.selectedConversation.messages[messageIndex] = action.payload;
          }
        }
        
        // Update in conversations cache
        Object.keys(state.messages).forEach(key => {
          const messagesArray = state.messages[key];
          if (messagesArray) { // Check if array exists
            const messageIndex = messagesArray.findIndex(
              msg => msg.id === action.payload.id
            );
            if (messageIndex >= 0) {
              messagesArray[messageIndex] = action.payload;
            }
          }
        });
      })
      .addCase(rejectNegotiation.rejected, (state, action) => {
        state.negotiationInProgress = false;
        state.negotiationError = action.payload as string;
      });
  }
});

// Export actions and reducer
export const { 
  clearSelectedConversation, 
  clearNegotiationState, 
  clearPendingPayment,
  setError
} = messageSlice.actions;

// Export selectors
export const selectMessages = (state: RootState) => state.messages;
export const selectChats = (state: RootState) => state.messages.chats;
export const selectSelectedConversation = (state: RootState) => state.messages.selectedConversation;
export const selectNegotiationState = (state: RootState) => ({
  inProgress: state.messages.negotiationInProgress,
  error: state.messages.negotiationError
});
export const selectPendingPayment = (state: RootState) => state.messages.pendingPayment;

export default messageSlice.reducer; 